import * as React from "react";
import { useAsync } from "react-use";
import { useApi } from "../../../../../containers/ApiContainer";
import { Ramp } from "../../../../../types";
import Canvas from "../../../../../widgets/useCanvas";

import { useActiveFBOs } from "../../../../../containers/ActiveFBOContainer";
import { useLayoutCanvasState } from "../../../../Ramp/RampPresenter";
import { drawMain } from "./drawing";
import { usePatterns } from "./patterns/usePatterns";

type Props = {
  ramp: Ramp;
  width: number;
  showReferenceImage: boolean;
  showSpots: boolean;
  zoomLevel: number;
};

export const RampCanvasReadOnlyNew: React.FC<Props> = ({
  ramp,
  width,
  showReferenceImage,
  showSpots,
  zoomLevel,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { x0, y0, setInitialPositionOffset } = useLayoutCanvasState();
  const rampImage = useAsync(async () => {
    if (ramp.id && showReferenceImage) {
      try {
        const { data } = await postgrest
          .from("ramp")
          .select("reference_image")
          .eq("id", ramp.id)
          .single();
        return data.reference_image;
      } catch (error) {
        console.error(
          `Error loading reference image for ramp: ${ramp.id}`,
          error
        );
        return null;
      }
    }
    return null;
  }, [showReferenceImage, ramp.reference_image]);

  const canvasRef = React.createRef<HTMLCanvasElement>();
  const canvasWidth = width;
  const canvasHeight = canvasWidth / (ramp.width / ramp.depth);
  const pixelsToFeetRatio = canvasHeight / (ramp?.depth ?? 1);
  const pixelsToFeet = (px: number) => px / pixelsToFeetRatio;

  const { loading: loadingPatterns, patterns } = usePatterns();

  // convert shapes to pixels and filter out spots if needed
  const shapes = React.useMemo(() => {
    const filteredShapes = showSpots
      ? ramp.shapes
      : ramp.shapes.filter(shape => shape.type !== 'Spot');

    return filteredShapes.map((shape) => ({
      ...shape,
      points: shape.points.map((point) => ({
        x: point.x * pixelsToFeetRatio,
        y: point.y * pixelsToFeetRatio,
      })),
    }));
  }, [ramp.shapes, pixelsToFeetRatio, showSpots]);

  const draw = React.useCallback(
    (context: CanvasRenderingContext2D) => {
      drawMain(context, {
        shapes,
        currentShape: null,
        highlightedShapes: [],
        mousePosition: null,
        calibrationPoints: ramp?.calibration_points,
        showCalibrationPoints: true, // Always show in read-only mode for now
        highlightedCalibrationPoint: null,
        canvasRef,
        pixelsToFeet,
        showMeasurements: false,
        showGuideLines: false,
        pixelsToFeetRatio,
        activeShapes: [],
        patterns,
        isShiftPressed: false,
        zoomLevel,
        isMetric: activeFBO?.settings?.useMetricSystem,
      });
    },
    [
      shapes,
      canvasRef,
      pixelsToFeet,
      pixelsToFeetRatio,
      patterns,
      ramp?.calibration_points,
    ]
  );

  React.useEffect(() => {
    // draw a square that is 100px by 100px in the viewable bottom-left corner of the ramp
    const xOffsetPixels = x0 * pixelsToFeetRatio;
    // how much of the y distance on the canvas is not visable in the viewport
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect?.bottom) {
      return;
    }

    const yOffsetPixels = Math.max(0, rect.bottom - window.innerHeight);
    setInitialPositionOffset({
      xFeet: xOffsetPixels / pixelsToFeetRatio,
      yFeet: yOffsetPixels / pixelsToFeetRatio,
    });
  }, [x0, y0, pixelsToFeetRatio]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      draw(context);
    }
  }, [canvasRef, draw]);

  // Simple check to prevent "undefined" in image URLs
  const safeImageUrl = React.useMemo(() => {
    if (
      rampImage.value &&
      typeof rampImage.value === "string" &&
      rampImage.value.includes("undefined")
    ) {
      console.warn(`Invalid URL with 'undefined' detected: ${rampImage.value}`);
      return null;
    }
    return rampImage.value;
  }, [rampImage.value]);

  if (loadingPatterns) {
    return null;
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Render background image as an IMG element for better html2canvas compatibility */}
      {showReferenceImage && safeImageUrl && (
        <img
          src={safeImageUrl}
          alt="Ramp reference background"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: canvasWidth,
            height: canvasHeight,
            objectFit: "cover",
            zIndex: 0,
            pointerEvents: "none", // Prevent interference with canvas interactions
          }}
          onError={(e) => console.error("Failed to load ramp background image:", e)}
        />
      )}
      <Canvas
        ref={canvasRef as React.RefObject<HTMLCanvasElement>}
        draw={draw}
        width={canvasWidth}
        height={canvasHeight}
        style={{
          position: "relative",
          zIndex: 1,
          backgroundColor: showReferenceImage ? "transparent" : undefined,
        }}
      />
    </div>
  );
};
